// composables/usePoolaRealtime.js
//
// Progressive-enhancement realtime for the Poola festival surfaces. Subscribes a
// component/composable to the ONE shared private channel per run —
// `PoolaDeal.{dealId}` — and forwards the three id-only broadcast events to the
// handlers the caller cares about. Polling (usePoolaDeal's boundary timer +
// usePoolaTeaser) remains the fallback when the socket never connects (tunnel
// mode); this layer only ever makes a working socket LIVE, never load-bearing.
//
// Contract (../backend/_bmad-output/sync/briefs/2026-07-10-poola-realtime-broadcast-BE-confirmation.md):
//   channel  private-PoolaDeal.{dealId}   (seller + supplier + prospect, PoolaViewer::forUser auth)
//   .PoolaOfferClaimed        { offer_id, pieces_claimed, pieces_remaining, status, timestamp }
//   .PoolaDealTotalsChanged   { deal_id, claimed_total_cents, purchase_count, timestamp }   (coalesced ~3s)
//   .PoolaDealStatusChanged   { deal_id, status, timestamp }
//
// ── Why a REFCOUNTED shared channel (not the bare reference pattern) ──────────
// OrderBoxTotalsPage/CartPage each own their channel alone, so they can
// `echo().leave(name)` on teardown. Here TWO independent subscribers ride the
// SAME channel and OVERLAP during the live phase:
//   • usePoolaDeal (page-lifetime)      → `.PoolaDealStatusChanged`
//   • PoolaRun (remounts per phase)     → `.PoolaOfferClaimed` + `.PoolaDealTotalsChanged`
// If PoolaRun called `echo().leave(name)` on unmount it would rip the channel out
// from under usePoolaDeal's still-mounted status listener. So each subscriber
// removes ONLY its own handlers (`channel.stopListening(event, fn)`), and the
// channel is left (`echo().leave(name)`) only when the LAST subscriber departs —
// tracked by a module-level per-channel refcount.
//
// ── Availability ─────────────────────────────────────────────────────────────
// Realtime is gated purely by auth: App.vue / useAuth call initRealtime() only
// for authenticated users, which is what configures Echo. `echo()` THROWS
// ("Echo has not been configured") when it never ran — unauthenticated boot,
// tunnel-with-no-socket, or unit tests. We treat `!echoIsConfigured()` as
// "realtime unavailable → no-op" so those paths fall through to polling cleanly.
import { echo, echoIsConfigured } from '@laravel/echo-vue';
import { onUnmounted, unref, watch } from 'vue';
import { isReplayOn } from '@/collector/previewReplay.js';
import { isRealtimeStarted, onReconnected } from '@/realtime/echo.js';

// Handler key → the leading-dot broadcastAs name (Echo strips the dot + prefixes
// the channel; the leading dot means "the literal broadcastAs, no namespace").
const EVENT_BY_HANDLER = {
  onClaim: '.PoolaOfferClaimed',
  onTotals: '.PoolaDealTotalsChanged',
  onStatus: '.PoolaDealStatusChanged',
};

// Module-level refcount per CHANNEL NAME. We deliberately do NOT cache the Echo
// channel object here: `echo().private(name)` is itself idempotent (pusher-js
// returns the same live channel by name), and re-deriving it every time keeps us
// correct across a teardownRealtime()/reconfigure (identity swap) that discards
// echo-vue's singleton — a cached object would go stale, a fresh lookup can't.
const refcounts = new Map(); // channelName -> number of live subscribers

/** dealId (value | ref | getter) → the private channel name, or null when blank. */
export function poolaChannelName(dealId) {
  const id = typeof dealId === 'function' ? dealId() : unref(dealId);
  return id == null || id === '' ? null : `PoolaDeal.${id}`;
}

/** Realtime is only usable once Echo has been configured (see file header). */
function realtimeReady() {
  // Never under an offline supplier preview (supplier-preview-offline L-J): the run is the phone's copy.
  if (isReplayOn()) return false;
  try {
    return echoIsConfigured();
  } catch {
    return false;
  }
}

/**
 * Test-only: drop all refcounts. The registry is module-global, so a test that
 * asserts mid-mount (without unmounting) could otherwise leak state into the
 * next. Not used in production.
 */
export function __resetPoolaRealtimeRegistry() {
  refcounts.clear();
}

/**
 * usePoolaRealtime — subscribe to a run's realtime channel for as long as the
 * calling component/composable is mounted (and to whichever run `dealId`
 * currently points at, re-subscribing if it changes).
 *
 * MUST be called from a setup() context — it registers a watcher + onUnmounted.
 * Every handler is OPTIONAL; only the events whose handler you pass are bound.
 * Handlers receive the raw event payload; the channel is already deal-scoped, so
 * a payload's own id fields are for defensive filtering only.
 *
 * @param {string | import('vue').Ref<string> | (() => string|null)} dealId
 *   The run id. May arrive async (a ref/getter) — we subscribe once it's non-null
 *   and re-subscribe if it changes; null/'' unsubscribes.
 * @param {object} handlers
 * @param {(e: object) => void} [handlers.onClaim]      `.PoolaOfferClaimed`
 * @param {(e: object) => void} [handlers.onTotals]     `.PoolaDealTotalsChanged`
 * @param {(e: object) => void} [handlers.onStatus]     `.PoolaDealStatusChanged`
 * @param {() => void}          [handlers.onReconnect]  fired on each socket RE-connect
 *   (gap-fill: refetch the authoritative slice this subscriber owns — a websocket
 *   has no replay buffer for frames missed while it was down).
 * @returns {{ leave: () => void }} manual teardown (also runs on unmount).
 */
export function usePoolaRealtime(dealId, handlers = {}) {
  const readName = () => poolaChannelName(dealId);

  let currentName = null; // the channel THIS instance currently holds (null = none)
  const bound = []; // [{ event, handler }] this instance added — for stopListening
  let unsubscribeReconnect = null;

  function subscribe(name) {
    if (name === currentName) return; // already on it → idempotent
    if (currentName !== null) leave(); // switched runs → drop the old channel first
    if (name == null || !realtimeReady()) return; // no run / no socket → polling covers it

    try {
      const first = (refcounts.get(name) ?? 0) === 0;
      const channel = echo().private(name);
      if (first) {
        // Attach the auth-error logger once per channel (not per subscriber) so a
        // subscription_error isn't logged N times on a shared channel.
        channel.error((err) => console.warn(`[poola-realtime] ${name} auth failed:`, err));
      }
      refcounts.set(name, (refcounts.get(name) ?? 0) + 1);
      // Claim the channel BEFORE the listen loop so a throw mid-bind rolls back
      // cleanly through leave() (decrement the refcount + stopListening whatever
      // we already bound) instead of leaking the count and orphaning listeners.
      currentName = name;

      for (const [key, event] of Object.entries(EVENT_BY_HANDLER)) {
        const fn = handlers[key];
        if (typeof fn !== 'function') continue;
        channel.listen(event, fn);
        bound.push({ event, handler: fn });
      }

      if (typeof handlers.onReconnect === 'function') {
        unsubscribeReconnect = onReconnected(handlers.onReconnect);
      }
    } catch (err) {
      // A subscribe failure must never break the page — polling still carries it.
      // leave() rolls back any partial subscription (refcount + bound handlers).
      console.warn(`[poola-realtime] subscribe(${name}) failed:`, err);
      leave();
    }
  }

  function leave() {
    if (currentName === null) return;
    const name = currentName;
    currentName = null;

    if (typeof unsubscribeReconnect === 'function') {
      unsubscribeReconnect();
      unsubscribeReconnect = null;
    }

    // Drop this subscriber's refcount regardless of socket state.
    const next = (refcounts.get(name) ?? 1) - 1;
    if (next <= 0) refcounts.delete(name);
    else refcounts.set(name, next);

    // Touch Echo ONLY while the socket is actually LIVE (isRealtimeStarted), NOT
    // merely configured. teardownRealtime() (logout / involuntary-401 / act-as)
    // discards the socket but re-runs configureEcho(), so echoIsConfigured() stays
    // TRUE — a configured-only guard would let echo().private(name) here
    // RECONSTRUCT an auto-connecting socket and re-subscribe the channel, firing a
    // spurious /broadcasting/auth as the DEPARTING user (the exact
    // lingering-authenticated-socket teardown exists to prevent). started=false
    // after teardown, so we skip Echo entirely and just drop bookkeeping.
    if (isRealtimeStarted()) {
      try {
        const channel = echo().private(name); // live cached channel (no reconstruct)
        // Remove ONLY this instance's handlers — a sibling may still ride the channel.
        for (const { event, handler } of bound) channel.stopListening(event, handler);
        if (next <= 0) echo().leave(name); // last subscriber out → unsubscribe the channel
      } catch (err) {
        console.warn(`[poola-realtime] leave(${name}) failed:`, err);
      }
    }
    bound.length = 0;
  }

  // Subscribe as soon as a run id exists (immediate covers a synchronously-known
  // id), and re-subscribe if it changes to a different run.
  watch(readName, (name) => subscribe(name), { immediate: true });

  onUnmounted(leave);

  return { leave };
}
